import { faQuestionCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { ReactNode } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeKatex from 'rehype-katex';
import remarkMath from 'remark-math';
import { KeyframeIndex, ParsedShaderSet, PropertyKeyframeSet, PropIndex, ShaderId } from './parser/parser';
import { PropertyValue } from './parser/propTypes';
import 'katex/dist/katex.min.css';
import rehypeRaw from 'rehype-raw';


// A help button that gives shader descriptions
interface ShaderHelpProps {
  description: string;
  onPopupWindow: (contents: ReactNode) => void;
}

export function ShaderHelp(props: ShaderHelpProps) {
  function popup() {
    props.onPopupWindow(
      <div className="shaderDescription">
        <ReactMarkdown
          remarkPlugins={[remarkMath]}
          rehypePlugins={[rehypeKatex, rehypeRaw]}
        >
          {props.description}
        </ReactMarkdown>
      </div>
    );
  }

  if(props.description !== "") {
    return (
      <div className="shaderHelp" onClick={popup}>
        <FontAwesomeIcon icon={faQuestionCircle}></FontAwesomeIcon>
      </div>
    );
  } else {
    return <></>;
  }
}

export interface ShaderPropsEditorProps {
  parsedShaders: ParsedShaderSet;
  activeShaders: ShaderId[];
  shaderPropKeyframes: PropertyKeyframeSet;
  shaderProps: PropertyValue[][];
  setShaderProp: (shader: ShaderId, propIndex: number, val: PropertyValue) => void;
  activeKeyframe: KeyframeIndex | null;

  addKeyframe: (prop: [ShaderId, PropIndex]) => void;
  deleteActiveKeyframe: () => void;

  onPopupWindow: (contents: ReactNode) => void;
  // enable / disable animation controls
  doAnimate: boolean;
  setDoAnimate: React.Dispatch<React.SetStateAction<boolean>>;
}

// An editor that allows the user to change property values for the active selected shaders.
// It just renders a list of each shader + inserts the appropriate propComponent for each property
export default function ShaderPropsEditor(props: ShaderPropsEditorProps) {
  const { parsedShaders, activeShaders, shaderProps, setShaderProp, shaderPropKeyframes, activeKeyframe, addKeyframe, deleteActiveKeyframe, onPopupWindow, doAnimate, setDoAnimate } = props;
  return (
    <div>
      {activeShaders.map((shaderId) => {
        const shader = parsedShaders.shaders[shaderId];
        // don't display the shader if it has no properties that can be edited and doesn't have a help description
        if(shader.properties.length === 0 && shader.description === "") return <span key={shaderId}></span>;
        
        return (
          <div key={shaderId} className="shaderPropsContainer">
            <div className="shaderHeader">
              <div className="shaderName">{shader.name}</div>
              <ShaderHelp description={shader.description} onPopupWindow={onPopupWindow}/>
            </div>
            {shader.properties.map((prop, propIndex) => {
              // check if we should display this prop
              const display = prop.conditionalPropIndex === null ? true :
                shaderProps[shaderId][prop.conditionalPropIndex].value;

              const Component = prop.editComponent();
              const value = shaderProps[shaderId][propIndex];
              const hasKeyframes = shaderPropKeyframes.keyframes[shaderId][propIndex].length > 0;
              const isActiveKeyframe = hasKeyframes && activeKeyframe !== null && activeKeyframe[0] === shaderId && activeKeyframe[1] === propIndex;

              const setValue = (value: PropertyValue) => { 
                setShaderProp(shaderId, propIndex, value);
              }
              if(display) {
                return (
                  <Component
                    key={propIndex}
                    name={prop.displayName}
                    value={value}
                    setValue={setValue}
                    args={prop.componentArgs()}
                    lockedConst={prop.alwaysConst()}
                    doAnimate={doAnimate}
                    enabled={!hasKeyframes || isActiveKeyframe}
                    activeKeyframe={isActiveKeyframe}
                    toggleKeyframe={() => {
                      if(isActiveKeyframe) {
                        deleteActiveKeyframe();
                      } else {
                        addKeyframe([shaderId, propIndex]);
                      }
                    }}
                  />
                );
              } else {
                return <span key={propIndex}></span>;
              }
            })}
          </div>
        );
      })}
      <div className="shaderPropsContainer">
        <div className="shaderHeader">
          <div className="shaderName">Animation</div>
        </div>
        <button onClick={() => setDoAnimate((animate) => !animate)}>
          {doAnimate ? "Disable Animation Controls" : "Enable Animation Controls"}
        </button>
      </div>
    </div>
  );
}